import { useContext, useEffect, useState } from "react";
import {
  Button,
  Form,
  Input,
  Row,
  Col,
  Select,
  InputNumber,
  Typography,
  Divider,
  Tag,
  Tooltip,
  Slider,
  Rate,
  Avatar,
  Space,
  Checkbox,
} from "antd";
import FormLayout, {
  FormContent,
  FormFooter,
  FormHeader,
} from "../../../../components/form/form";
import { TKPI } from "../../../../types/config";
import {
  eEVALUATION_CRITERIA,
  ePERFORMANCE_RATING,
  eResultCode,
} from "../../../../utils/enum";
import { AddEditAssessment, GetKPIs } from "../../../../utils/api.constant";
import useFetch from "../../../../hooks/useFetch";
import { PERFORMANCE_RATING_LABELS } from "../../../../utils/constants";
import {
  CheckOutlined,
  CloseOutlined,
  InfoCircleOutlined,
  UserOutlined,
  MailOutlined,
  SafetyCertificateOutlined,
  BankOutlined,
  MobileOutlined,
} from "@ant-design/icons";
import useToast from "../../../../hooks/useToast";
import { ToastOpen, ToastType } from "../../../../state/toast/slice";
import utils from "../../../../utils";
import { UserContext } from "../../../../context/user";
import TextArea from "antd/es/input/TextArea";

const { Text, Title } = Typography;

type TDrawerProps = {
  id: number;
  onCloseDrawer: () => void;
  onRefreshList: () => void;
};

const PercentageInput = ({
  value,
  disabled,
  onChange,
}: {
  value?: number;
  disabled?: boolean;
  onChange?: (value: number | null) => void;
}) => {
  return (
    <Row gutter={12} align="middle">
      <Col flex="auto">
        <Slider
          min={0}
          max={100}
          value={typeof value === "number" ? value : 0}
          onChange={onChange}
          disabled={disabled}
          marks={{ 0: "0%", 25: "25%", 50: "50%", 75: "75%", 100: "100%" }}
        />
      </Col>
      <Col flex="80px">
        <InputNumber
          min={0}
          max={100}
          value={value}
          onChange={onChange}
          disabled={disabled}
          style={{ width: "100%" }}
          formatter={(value) => `${value}%`}
          parser={(value) => value?.replace("%", "") as unknown as number}
        />
      </Col>
    </Row>
  );
};

const TimeInput = ({
  value,
  disabled,
  onChange,
}: {
  value?: { hours?: number; minutes?: number } | string;
  disabled?: boolean;
  onChange?: (value: string) => void;
}) => {
  // Parse initial value if it's a string "HH:MM"
  let initialHours = 0;
  let initialMinutes = 0;

  if (typeof value === "string" && value.includes(":")) {
    const parts = value.split(":");
    initialHours = parseInt(parts[0], 10);
    initialMinutes = parseInt(parts[1], 10);
  } else if (typeof value === "object") {
    initialHours = value?.hours || 0;
    initialMinutes = value?.minutes || 0;
  }

  const [hours, setHours] = useState<number | null>(initialHours || null);
  const [minutes, setMinutes] = useState<number | null>(initialMinutes || null);

  const handleTimeChange = (
    newHours: number | null,
    newMinutes: number | null,
  ) => {
    setHours(newHours);
    setMinutes(newMinutes);
    if (newHours !== null && newMinutes !== null) {
      if (onChange) {
        onChange(
          `${newHours.toString().padStart(2, "0")}:${newMinutes
            .toString()
            .padStart(2, "0")}`,
        );
      }
    } else {
      if (onChange) onChange("");
    }
  };

  return (
    <Row gutter={8}>
      <Col span={12}>
        <InputNumber
          min={0}
          placeholder="HH"
          value={hours}
          onChange={(val) => handleTimeChange(val, minutes)}
          style={{ width: "100%" }}
          addonAfter="h"
        />
      </Col>
      <Col span={12}>
        <InputNumber
          min={0}
          max={59}
          placeholder="MM"
          value={minutes}
          onChange={(val) => handleTimeChange(hours, val)}
          style={{ width: "100%" }}
          addonAfter="m"
        />
      </Col>
    </Row>
  );
};

const AmountInput = ({
  value,
  onChange,
}: {
  value?: number;
  onChange?: (value: number | null) => void;
}) => {
  return (
    <InputNumber
      style={{ width: "100%" }}
      formatter={(value) => `₹ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
      parser={(value) => value?.replace(/\₹\s?|(,*)/g, "") as unknown as number}
      value={value}
      onChange={onChange}
      placeholder="Enter Amount"
    />
  );
};

export default function PerformanceForm(props: TDrawerProps) {
  const [form] = Form.useForm();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const { onShowToast } = useToast();
  const { post } = useFetch();
  const [kpiList, setKpiList] = useState<TKPI[]>([]);
  const { user } = useContext(UserContext);
  const [isSubmitted, setIsSubmitted] = useState(false);

  useEffect(() => {
    fetchKPIs();
  }, []);

  const fetchKPIs = async () => {
    setIsLoading(true);
    try {
      const payload = {
        data: {
          pageSize: -1,
          designationIds: [user?.designation], // Static for now as requested
        },
      };
      const response = await post(GetKPIs, payload);
      if (response.dataResponse.returnCode === eResultCode.SUCCESS) {
        const data = response.data;

        setKpiList(data);

        const initialValues: any = {};
        let submitted = false;

        data.forEach((kpi: any) => {
          if (kpi.value !== null && kpi.value !== undefined) {
            submitted = true;
          }

          initialValues[kpi.id] = kpi.value;
          initialValues[`remark_${kpi.id}`] = kpi.self_comment;
        });

        setIsSubmitted(submitted);

        form.setFieldsValue(initialValues);

      } else {
        onShowToast({
          type: ToastType.error,
          title: <CloseOutlined />,
          position: ToastOpen.centerTop,
          content: response.dataResponse.description,
        });
      }
    } catch (error) {
      console.error("Error fetching KPIs:", error);
      onShowToast({
        type: ToastType.error,
        title: <CloseOutlined />,
        position: ToastOpen.centerTop,
        content: "Failed to fetch KPIs",
      });
    } finally {
      setIsLoading(false);
    }
  };


  const onFinish = async (values: any) => {
    try {
      const kpiDetails = kpiList.map((kpi: any) => {
        let formattedValue = values[kpi.id];

        // convert hh:mm to object
        if (
          typeof formattedValue === "string" &&
          formattedValue.includes(":")
        ) {
          const [h, m] = formattedValue.split(":");
          formattedValue = {
            h: Number(h),
            m: Number(m),
          };
        }

        return {
          kpiId: Number(kpi.id),
          value: formattedValue,
          isRemark: true,
          self_comment: values[`remark_${kpi.id}`] || null,
        };
      });

      const payload = {
        data: {
          userId: user?.id,
          id: props.id ? props.id : 0,
          kpiDetails: kpiDetails,
        },
      };

      setIsLoading(true);
      console.log(payload);

      const response = await post(AddEditAssessment, payload);

      if (response.dataResponse.returnCode === eResultCode.SUCCESS) {
        onShowToast({
          type: ToastType.success,
          title: <CheckOutlined />,
          position: ToastOpen.centerTop,
          content: response.dataResponse.description,
        });

        form.resetFields();
        props.onRefreshList();
        props.onCloseDrawer();
      } else {
        onShowToast({
          type: ToastType.error,
          title: <CloseOutlined />,
          position: ToastOpen.centerTop,
          content: response.dataResponse.description,
        });
      }
    } catch (error) {
      console.error("Error:", error);
      setIsLoading(false);
    }
  };
  const getCriteriaLabel = (criteria?: number) => {
    if (!criteria) return "";
    return utils.formatEnumLabel(eEVALUATION_CRITERIA[criteria]);
  };

  const renderInputByCriteria = (kpi: TKPI) => {
    const { criteria } = kpi;

    switch (criteria) {
      case eEVALUATION_CRITERIA.PERCENTAGE:
        return <PercentageInput disabled={isSubmitted} />;
      case eEVALUATION_CRITERIA.RATING:
        return <Rate allowHalf disabled={isSubmitted} />;
      case eEVALUATION_CRITERIA.TIME:
        return <TimeInput disabled={isSubmitted} />;
      case eEVALUATION_CRITERIA.SELECTION:
        const options = Object.keys(PERFORMANCE_RATING_LABELS).map((key) => ({
          label: PERFORMANCE_RATING_LABELS[Number(key) as ePERFORMANCE_RATING],
          value: Number(key),
        }));
        return (
          <Select
            options={options}
            placeholder="Select Rating"
            allowClear
            menuItemSelectedIcon={<CheckOutlined />}
            dropdownStyle={{ minWidth: 250 }}
          />
        );
      case eEVALUATION_CRITERIA.NUMBER:
        return (
          <InputNumber
            min={0}
            placeholder="Enter value"
            style={{ width: "100%" }}
          />
        );
      case eEVALUATION_CRITERIA.AMOUNT:
        return <AmountInput />;
      default:
        return <Input placeholder="Enter value" />;
    }
  };

  const renderUserDetails = () => (
    <div
      style={{
        height: "100%",
        padding: "24px",
        backgroundColor: "#f9fafb",
        borderRight: "1px solid #f0f0f0",
        display: "flex",
        flexDirection: "column",
        gap: "24px",
      }}
    >
      <div style={{ textAlign: "center" }}>
        <Avatar
          size={80}
          icon={<UserOutlined />}
          style={{
            backgroundColor: "var(--bs-primary)",
            marginBottom: "16px",
            boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
          }}
        >
          {user?.fullName
            ?.split(" ")
            .map((n) => n[0])
            .join("")}
        </Avatar>
        <Title level={4} style={{ marginBottom: "4px", color: "#1f2937" }}>
          {user?.fullName || "Guest User"}
        </Title>
        <Tag color="var(--bs-primary)" bordered={false}>
          {user?.designationTitle || "No Designation"}
        </Tag>
      </div>

      <Divider style={{ margin: "12px 0" }} />

      <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
        <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
          <div
            style={{
              padding: "8px",
              backgroundColor: "#e6f7ff",
              borderRadius: "50%",
              color: "var(--bs-primary-dark)",
            }}
          >
            <MailOutlined />
          </div>
          <div style={{ display: "flex", flexDirection: "column" }}>
            <Text type="secondary" style={{ fontSize: "12px" }}>
              Email Address
            </Text>
            <Text strong style={{ fontSize: "13px" }}>
              {user?.emailId || "N/A"}
            </Text>
          </div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
          <div
            style={{
              padding: "8px",
              backgroundColor: "#e6f7ff",
              borderRadius: "50%",
              color: "var(--bs-primary-dark)",
            }}
          >
            <MobileOutlined />
          </div>
          <div style={{ display: "flex", flexDirection: "column" }}>
            <Text type="secondary" style={{ fontSize: "12px" }}>
              Mobile Number
            </Text>
            <Text strong style={{ fontSize: "13px" }}>
              {user?.mobileNumber || "N/A"}
            </Text>
          </div>
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
          <div
            style={{
              padding: "8px",
              backgroundColor: "#f6ffed",
              borderRadius: "50%",
              color: "var(--bs-primary-dark)",
            }}
          >
            <BankOutlined />
          </div>
          <div style={{ display: "flex", flexDirection: "column" }}>
            <Text type="secondary" style={{ fontSize: "12px" }}>
              Organization
            </Text>
            <Text strong style={{ fontSize: "13px" }}>
              {user?.org?.name || "N/A"}
            </Text>
          </div>
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
          <div
            style={{
              padding: "8px",
              backgroundColor: "#fff7e6",
              borderRadius: "50%",
              color: "var(--bs-primary-dark)",
            }}
          >
            <SafetyCertificateOutlined />
          </div>
          <div style={{ display: "flex", flexDirection: "column" }}>
            <Text type="secondary" style={{ fontSize: "12px" }}>
              Role
            </Text>
            <Text strong style={{ fontSize: "13px" }}>
              {user?.roles?.name || "N/A"}
            </Text>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div style={{ display: "flex", height: "100%", width: "100%" }}>
      <div style={{ width: "20%", minWidth: "250px", height: "100%" }}>
        {renderUserDetails()}
      </div>
      <div style={{ width: "80%", height: "100%" }}>
        <FormLayout>
          <Form
            form={form}
            name="performance_form"
            onFinish={onFinish}
            layout="vertical"
            requiredMark="optional"
          >
            <FormHeader>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <CloseOutlined
                  style={{ cursor: "pointer", color: "white" }}
                  onClick={props.onCloseDrawer}
                />
                <span style={{ color: "white", fontSize: 16, fontWeight: 500 }}>
                  Assessment
                </span>
              </div>
            </FormHeader>

            {/* <FormContent>
              <Row gutter={[16, 16]}>
                {kpiList.map((kpi, index) => (
                  <Col span={12} key={kpi.id}>
                    <div
                      style={{
                        height: "100%",
                        padding: "16px",
                        backgroundColor: "#f9f9f9",
                        borderRadius: "8px",
                        border: "1px solid #f0f0f0",
                        display: "flex",
                        flexDirection: "column",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          marginBottom: 8,
                          alignItems: "flex-start",
                        }}
                      >
                        <Text
                          strong
                          style={{
                            fontSize: 14,
                            marginRight: 8,
                            flex: 1,
                          }}
                        >
                          {index + 1}. {kpi.name}
                        </Text>
                        {kpi.criteria && (
                          <Tag color="black" style={{ margin: 0 }}>
                            {getCriteriaLabel(kpi.criteria)}
                          </Tag>
                        )}
                      </div>
                      <div
                        style={{
                          marginBottom: 12,
                          color: "#666",
                          fontSize: 12,
                          flex: 1,
                        }}
                      >
                        {kpi.description}
                      </div>
                      <Form.Item
                        name={`${kpi.id}`}
                        rules={[
                          {
                            required: true,
                            message: "This field is required",
                          },
                        ]}
                        style={{ marginBottom: 0 }}
                      >
                        {renderInputByCriteria(kpi)}
                        <Checkbox onChange={() => toggleRemark(kpi.id)}>
                          Add Remark
                        </Checkbox>

                        {remarkVisible[kpi.id] && (
                          <Form.Item
                            name={`remark_${kpi.id}`}
                            style={{ marginTop: 8 }}
                          >
                            <TextArea rows={2} placeholder="Enter remark..." />
                          </Form.Item>
                        )}
                      </Form.Item>

                    </div>
                  </Col>
                ))}
              </Row>
            </FormContent> */}
            <FormContent>
              <Row gutter={[16, 16]}>
                {kpiList.map((kpi, index) => (
                  <Col span={12} key={kpi.id}>
                    <div
                      style={{
                        padding: "16px",
                        backgroundColor: "#f9f9f9",
                        borderRadius: "8px",
                        border: "1px solid #f0f0f0",
                        height: "100%",
                      }}
                    >
                      {/* HEADER */}
                      <Row justify="space-between" align="middle">
                        <Col flex="auto">
                          <Text strong style={{ fontSize: 14 }}>
                            {index + 1}. {kpi.name}
                          </Text>
                        </Col>

                        {/* {kpi.criteria && (
                          <Col>
                            <Tag color="black">
                              {getCriteriaLabel(kpi.criteria)}
                            </Tag>
                          </Col>
                        )} */}
                      </Row>

                      {/* DESCRIPTION */}
                      <Row style={{ marginTop: 8 }}>
                        <Col span={24}>
                          <Text style={{ fontSize: 12, color: "#666" }}>
                            {kpi.description}
                          </Text>
                        </Col>
                      </Row>

                      {/* INPUT FIELD */}
                      <Row style={{ marginTop: 12 }}>
                        <Col span={24}>
                          <Form.Item
                            name={`${kpi.id}`}
                            rules={[
                              { required: true, message: "This field is required" },
                            ]}
                          >
                            {renderInputByCriteria(kpi)}
                          </Form.Item>
                        </Col>
                      </Row>

                      {/* REMARK TEXTAREA */}
                      <Row style={{ marginTop: 8 }}>
                        <Col span={24}>
                          <Form.Item 
                            name={`remark_${kpi.id}`}
                            rules={[{ required: true, message: "Remark is required" }]}
                          >
                            <TextArea rows={2} placeholder="Enter remark..." disabled={isSubmitted} />
                          </Form.Item>
                        </Col>
                      </Row>
                    </div>
                  </Col>
                ))}
              </Row>
            </FormContent>

            <FormFooter className="formFooter">
              <Button
                onClick={props.onCloseDrawer}
                disabled={isLoading}
              // className="btnDimensions"
              >
                Close
              </Button>
              <Button
                type="primary"
                onClick={form.submit}
                loading={isLoading}
                disabled={isSubmitted}
              // className="btnDimensions"
              >
                Submit Assessment
              </Button>
            </FormFooter>
          </Form>
        </FormLayout>
      </div>
    </div>
  );
}
